import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../services/AuthContext'
import apiService from '../services/api'
import type { Subscription } from '../types'
import { logCheckout } from '../lib/subscribeCheckoutDebug'

/** True once PayPal webhooks have synced the DB (handles new subs + plan-change with same PayPal subscription id). */
function isCheckoutSynced(
  sub: Subscription | null,
  initialPlan: string | undefined,
  expectedPayPalSubscriptionId: string | null
): boolean {
  if (!sub) return false
  if (expectedPayPalSubscriptionId) {
    if (sub.paypalSubscriptionId !== expectedPayPalSubscriptionId) return false
    if (!initialPlan || initialPlan === 'free') return sub.plan !== 'free'
    return sub.plan !== initialPlan
  }
  return sub.plan !== 'free' && !!sub.paypalSubscriptionId
}

const AccountPage: React.FC = () => {
  const location = useLocation()
  const { user, subscription, quotaStatus, logout, refreshAuth } = useAuth()
  const [processing, setProcessing] = useState(false)
  const [processingMessage, setProcessingMessage] = useState('')

  // Helper function to check if client is Premiere - use everywhere
  const isPremiereClient = () => {
    return localStorage.getItem('auth_client') === 'premiere'
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const paypalReturn =
      (urlParams.get('success') === 'true' && urlParams.get('processing') === 'true') ||
      !!urlParams.get('subscription_id') ||
      !!urlParams.get('ba_token')

    logCheckout('account.landing', {
      fullUrl: window.location.href,
      success: urlParams.get('success'),
      processing: urlParams.get('processing'),
      upgraded: urlParams.get('upgraded'),
      paypalReturn,
      auth_client: localStorage.getItem('auth_client'),
    })

    // PayPal return: poll with fresh API reads (do not use `subscription` from closure — it stays stale).
    if (paypalReturn) {
      const expectedPayPalId = urlParams.get('subscription_id')
      let cancelled = false

      const runDesktopRedirectIfNeeded = () => {
        if (isPremiereClient()) {
          window.history.replaceState({}, document.title, window.location.pathname)
          localStorage.removeItem('auth_client')
          return
        }
        const token = localStorage.getItem('authToken')
        const refreshToken = localStorage.getItem('refreshToken')
        const client = localStorage.getItem('auth_client')
        if (token && refreshToken && (client === 'desktop' || !client)) {
          const protocolUrl = `ema://auth?token=${encodeURIComponent(token)}&refresh_token=${encodeURIComponent(refreshToken)}`
          window.location.href = protocolUrl
        }
        window.history.replaceState({}, document.title, window.location.pathname)
      }

      const poll = async () => {
        setProcessing(true)
        setProcessingMessage(
          'Processing your payment — confirming your subscription with PayPal. This usually takes a few seconds…'
        )

        let initialPlan: string | undefined
        try {
          await refreshAuth()
          const first = await apiService.getSubscription()
          initialPlan = first?.plan
        } catch {
          initialPlan = undefined
        }

        const maxAttempts = 50
        const delayMs = 2000

        for (let i = 0; i < maxAttempts && !cancelled; i++) {
          if (i > 0) {
            await new Promise((r) => setTimeout(r, delayMs))
          }
          try {
            await refreshAuth()
          } catch {
            await new Promise((r) => setTimeout(r, 3000))
            continue
          }
          let sub: Subscription | null = null
          try {
            sub = await apiService.getSubscription()
          } catch {
            continue
          }
          if (isCheckoutSynced(sub, initialPlan, expectedPayPalId)) {
            if (cancelled) return
            try {
              await refreshAuth()
            } catch {
              /* ignore */
            }
            if (cancelled) return
            setProcessing(false)
            setProcessingMessage('')
            runDesktopRedirectIfNeeded()
            return
          }
        }

        if (!cancelled) {
          setProcessingMessage(
            'PayPal is taking longer than usual to confirm. Your subscription may already be active — try refreshing this page in a moment.'
          )
        }
      }

      void poll()
      return () => {
        cancelled = true
      }
    }

    // Immediate upgrade (e.g. free plan path — no PayPal return params)
    if (urlParams.get('upgraded') === 'true') {
      if (isPremiereClient()) {
        window.history.replaceState({}, document.title, window.location.pathname)
        localStorage.removeItem('auth_client')
        return
      }

      setProcessing(true)
      setProcessingMessage('Your subscription has been upgraded successfully!')

      refreshAuth()
        .then(() => {
          setProcessing(false)
          setProcessingMessage('')

          if (isPremiereClient()) {
            window.history.replaceState({}, document.title, window.location.pathname)
            localStorage.removeItem('auth_client')
            return
          }

          const token = localStorage.getItem('authToken')
          const refreshToken = localStorage.getItem('refreshToken')
          const client = localStorage.getItem('auth_client')

          if (token && refreshToken && (client === 'desktop' || !client)) {
            const protocolUrl = `ema://auth?token=${encodeURIComponent(token)}&refresh_token=${encodeURIComponent(refreshToken)}`
            window.location.href = protocolUrl
          }

          window.history.replaceState({}, document.title, window.location.pathname)
        })
        .catch(() => {
          setProcessingMessage('Upgrade completed, but there was an issue refreshing your account. Please refresh the page.')
        })
    }
  }, [location.search, refreshAuth])

  const handleLogout = () => {
    logout()
    window.location.href = '/'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'Free Plan'
      case 'basic':
        return 'Basic Plan'
      case 'pro':
        return 'Pro Plan'
      case 'enterprise':
        return 'Enterprise Plan'
      default:
        return plan
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981'
      case 'canceled':
        return '#ef4444'
      case 'past_due':
        return '#f59e0b'
      default:
        return '#6b7280'
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '2rem 0' }}>
      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          
          {/* Processing Message */}
          {processing && (
            <div style={{
              background: '#dbeafe',
              border: '1px solid #3b82f6',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '0.5rem' }}>
                🔄 Processing your payment
              </div>
              <div style={{ color: '#1e40af' }}>
                {processingMessage}
              </div>
            </div>
          )}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '2rem' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Link to="/" style={{ lineHeight: 0 }}>
                <img src="/emma-icon.svg" alt="" width={64} height={64} />
              </Link>
              <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>Account</h1>
            </div>
            <button
              onClick={handleLogout}
              className="btn btn-secondary"
            >
              Logout
            </button>
          </div>


          {/* User Info */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Profile Information</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Email
                </div>
                <div style={{ fontSize: '1.125rem' }}>{user?.email}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Role
                </div>
                <div style={{ fontSize: '1.125rem', textTransform: 'capitalize' }}>
                  {user?.role}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Member Since
                </div>
                <div style={{ fontSize: '1.125rem' }}>
                  {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Info */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Subscription</h2>
              <a href="/subscribe" className="btn btn-primary">
                {subscription ? 'Manage Subscription' : 'Upgrade Plan'}
              </a>
            </div>
            
            {subscription ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Plan
                  </div>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                    {getPlanDisplayName(subscription.plan)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Status
                  </div>
                  <div style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: 'bold',
                    color: subscription.plan === 'free' ? '#10b981' : getStatusColor(subscription.status)
                  }}>
                    {subscription.plan === 'free' ? 'Active' : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                  </div>
                </div>
                {subscription.plan !== 'free' && subscription.currentPeriodEnd && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Next Billing Date
                    </div>
                    <div style={{ fontSize: '1.125rem' }}>
                      {formatDate(subscription.currentPeriodEnd)}
                    </div>
                  </div>
                )}
                {subscription.plan === 'free' && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Billing
                    </div>
                    <div style={{ fontSize: '1.125rem', color: '#10b981' }}>
                      No billing required
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                <p>You're currently on the free plan.</p>
                <p>Upgrade to unlock unlimited AI-powered video editing!</p>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          {quotaStatus && (
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '2rem'
            }}>
              <h2 style={{ marginBottom: '1rem' }}>Usage This Period</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Input Tokens
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {quotaStatus.usage.inputTokens.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    of {quotaStatus.limits.inputTokens.toLocaleString()} used
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    background: '#e5e7eb', 
                    borderRadius: '4px', 
                    marginTop: '0.5rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(quotaStatus.usage.inputTokens / quotaStatus.limits.inputTokens) * 100}%`,
                      height: '100%',
                      background: quotaStatus.usage.inputTokens >= quotaStatus.limits.inputTokens ? '#ef4444' : '#3b82f6',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Output Tokens
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {quotaStatus.usage.outputTokens.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    of {quotaStatus.limits.outputTokens.toLocaleString()} used
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    background: '#e5e7eb', 
                    borderRadius: '4px', 
                    marginTop: '0.5rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(quotaStatus.usage.outputTokens / quotaStatus.limits.outputTokens) * 100}%`,
                      height: '100%',
                      background: quotaStatus.usage.outputTokens >= quotaStatus.limits.outputTokens ? '#ef4444' : '#3b82f6',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                Resets on {formatDate(quotaStatus.resetTime)}
              </div>
            </div>
          )}

          {/* Desktop App Token */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Desktop App Authentication</h2>
            <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem' }}>
              If the automatic upgrade flow didn't work, you can manually copy your authentication tokens to the desktop app.
            </p>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Auth Token
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={localStorage.getItem('authToken') || ''}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      backgroundColor: '#f9fafb'
                    }}
                  />
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('authToken');
                      if (token) {
                        navigator.clipboard.writeText(token);
                        alert('Auth token copied to clipboard!');
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Refresh Token
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={localStorage.getItem('refreshToken') || ''}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      backgroundColor: '#f9fafb'
                    }}
                  />
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('refreshToken');
                      if (token) {
                        navigator.clipboard.writeText(token);
                        alert('Refresh token copied to clipboard!');
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: 'bold' }}>Instructions:</h4>
              <ol style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                <li>Copy both tokens above</li>
                <li>Open the Emma Desktop app</li>
                <li>Go to Settings → Authentication</li>
                <li>Paste the tokens and save</li>
                <li>Your desktop app will now have access to your subscription</li>
              </ol>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Quick Actions</h2>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <a href="/subscribe" className="btn btn-primary">
                {subscription ? 'Manage Subscription' : 'Upgrade Plan'}
              </a>
              <a href="/" className="btn btn-secondary">
                Download Desktop App
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountPage
